import { MenuConfig } from "@/config/types";
import {
  Bolt,
  Users,
  Grid,
  Building2,
  Ship,
  UserCheck,
  Shield,
  Kanban,
  FileText,
  GitBranch,
  Inbox,
  LayoutDashboard,
  Settings,
  RefreshCw,
  Clock,
  DollarSign,
  MapPin,
  Factory,
  ClipboardList,
  Cog,
  Truck,
  Activity,
  UserPlus,
  Landmark,
  Sparkles,
  HardHat,
} from "lucide-react";

// =============================================================================
// Types
// =============================================================================

export interface ModuleMenuConfig {
  dashboard: MenuConfig;
  /** defaultCollapsed — section renders with its accordion closed (#908). */
  sections: Array<{ id: string; menu: MenuConfig; defaultCollapsed?: boolean }>;
  placeholder?: string;
}

// =============================================================================
// PLATFORM MENU (SaaS Admin)
// =============================================================================

export const MENU_SIDEBAR_MAIN: MenuConfig = [
  {
    children: [
      { title: 'Dashboard', path: '/dashboard', icon: Bolt },
      { title: 'Tenants', path: '/platform/tenants', icon: Building2 },
      { title: 'Modules', path: '#', icon: Grid },
    ],
  }
];

// =============================================================================
// MODULE_CONFIG - Single source of truth for all tenant module menus
// =============================================================================

export const MODULE_CONFIG: Record<string, ModuleMenuConfig> = {
  // Dashboard Module
  dashboard: {
    dashboard: [{ children: [{ title: 'Dashboard', path: '/dashboard', icon: LayoutDashboard }] }],
    sections: [
      { 
        id: 'people', 
        menu: [{
          title: 'People',
          children: [
            { title: 'Employees', path: '/employees', icon: UserCheck },
            { title: 'Roles & Permissions', path: '/settings/roles', icon: Shield },
          ]
        }]
      },
      { 
        id: 'administration', 
        menu: [{
          title: 'Administration',
          children: [
            { title: 'Dealership Settings', path: '/settings/dealership', icon: Building2 },
            { title: 'Templates', path: '/settings/templates', icon: FileText },
            { title: 'Inbound Email', path: '/settings/inbound-email', icon: Inbox },
          ]
        }]
      },
    ],
  },

  // CRM Module
  crm: {
    dashboard: [{ children: [{ title: 'CRM Dashboard', path: '/crm', icon: LayoutDashboard }] }],
    sections: [
      {
        id: 'my-work',
        menu: [{
          title: 'My Work',
          children: [
            { title: 'Activity', path: '/crm/activity', icon: Activity },
          ]
        }]
      },
      {
        id: 'inbox',
        menu: [{
          title: 'Inbox',
          children: [
            { title: 'Inquiries', path: '/crm/enquiries', icon: Inbox },
          ]
        }]
      },
      {
        id: 'contacts',
        menu: [{
          title: 'Contacts',
          children: [
            // Leads first — the working queue outranks the address book.
            // #908 — same page with the leads segment locked; a real path so
            // the sidebar active state matches by pathname.
            { title: 'Leads', path: '/crm/leads', icon: UserPlus },
            { title: 'Customers', path: '/crm/customers', icon: Users },
          ]
        }]
      },
      {
        id: 'deals',
        menu: [{
          title: 'Deals',
          children: [
            { title: 'All Deals', path: '/crm/deals', icon: Users },
            { title: 'Kanban Board', path: '/crm/deals/kanban', icon: Kanban },
          ]
        }]
      },
      {
        id: 'sales',
        defaultCollapsed: true,
        menu: [{
          title: 'Sales',
          children: [
            // Hidden per #905 (quotes are reached from the record, not a
            // flat list); the entry is retained so /crm/quotes keeps its
            // breadcrumb and the route stays reachable by URL.
            { title: 'Quotes', path: '/crm/quotes', icon: FileText, hidden: true },
            { title: 'Pipelines', path: '/crm/pipelines', icon: GitBranch },
          ]
        }]
      },
    ],
  },

  // Inventory Module
  inventory: {
    // #1406 — the dashboard and the fixed reports need inventory.view (the
    // API returns 403 without it). UX filter only; the server enforces.
    dashboard: [{ children: [{ title: 'Inventory Dashboard', path: '/inventory', icon: LayoutDashboard, requiredPermission: 'inventory.view' }] }],
    sections: [
      {
        id: 'inventory',
        menu: [{
          title: 'Inventory',
          children: [
            { title: 'All Boats', path: '/inventory/boats', icon: Ship },
            { title: 'All Engines', path: '/inventory/engines', icon: Cog },
            { title: 'All Trailers', path: '/inventory/trailers', icon: Truck },
          ]
        }]
      },
      {
        id: 'inventory-reports',
        menu: [{
          title: 'Reports',
          requiredPermission: 'inventory.view',
          children: [
            { title: 'Summary', path: '/inventory/reports/summary', icon: ClipboardList },
            { title: 'Days on Market', path: '/inventory/reports/days-on-market', icon: Clock },
            { title: 'Pricing Analysis', path: '/inventory/reports/pricing', icon: DollarSign },
            { title: 'By Location', path: '/inventory/reports/by-location', icon: MapPin },
            { title: 'By Manufacturer', path: '/inventory/reports/by-manufacturer', icon: Factory },
          ]
        }]
      },
      {
        id: 'inventory-administration',
        menu: [{
          title: 'Administration',
          children: [
            { title: 'Inventory Settings', path: '/inventory/settings', icon: Settings },
            { title: 'Website Sync', path: '/inventory/wp-sync', icon: RefreshCw },
          ]
        }]
      },
    ],
  },

  // Service Module
  // D9-04: Work Orders is the module's only sidebar entry. The
  // Phase 15 (D15-23) adds a second entry, Finance Sync, shown only to
  // employees holding service.accounting_sync.view (UX filter only).
  // Phase 18 (D18-02) adds Subcontractors, shown only with
  // service.subcontractors.view — UX filter only.
  // ADVISOR-DESKTOP.md §10.2 Estimates/Inspections/Messages/Reminders/Reports
  // entries are deliberately NOT added here — none of those pages exist in
  // v1.1, and a dead sidebar link is worse than a missing one.
  service: {
    dashboard: [
      {
        children: [
          { title: 'Work Orders', path: '/service/work-orders', icon: ClipboardList },
          {
            title: 'Finance Sync',
            path: '/service/finance-sync',
            icon: Landmark,
            requiredPermission: 'service.accounting_sync.view',
          },
          {
            title: 'Subcontractors',
            path: '/service/subcontractors',
            icon: HardHat,
            requiredPermission: 'service.subcontractors.view',
          },
        ],
      },
    ],
    sections: [],
  },

  // Finance Module (placeholder)
  finance: {
    dashboard: [{ children: [{ title: 'Finance Dashboard', path: '/finance', icon: LayoutDashboard }] }],
    sections: [],
    placeholder: 'Finance module coming soon',
  },

  // Reporting Module (AI Reporting, PAGE-01)
  reporting: {
    dashboard: [{ children: [{ title: 'AI Reporting', path: '/reporting', icon: Sparkles }] }],
    sections: [],
  },
};

// Legacy exports for backwards compatibility
export const MENU_SIDEBAR_TENANT: MenuConfig = MODULE_CONFIG.dashboard.dashboard;
export const MENU_TENANT_MAIN: MenuConfig = MODULE_CONFIG.dashboard.dashboard;
